import { useEffect, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { completeFanOnboarding } from "../../services/pipService";
import { usePipOptional } from "./PipContext";
import PipCharacter from "./PipCharacter";

const STEPS = [
  {
    mood: "wave",
    peek: null,
    body: "Hey — I'm Pip. One thing to know, ten seconds.",
    action: "Let's go",
  },
  {
    mood: "idle",
    peek: null,
    body: "Every drop only lives for 100 views. After that it's gone — if you like it, unlock it before the last view.",
    action: "Next",
  },
  {
    mood: "peek",
    peek: "A",
    body: "I'm Pip — find me hiding around the platform for bonus credits. Good luck 🍁",
    action: "Got it!",
  },
];

export default function PipOnboarding() {
  const { currentUser, userPerks, refreshUserPerks } = useAuth();
  const pip = usePipOptional();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!currentUser || !userPerks) return;
    if (userPerks.fanOnboardingComplete) return;
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [currentUser, userPerks]);

  if (!visible) return null;

  const current = STEPS[step] || STEPS[0];
  const isLast = step === STEPS.length - 1;

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    setVisible(false);
    try {
      const result = await completeFanOnboarding(userPerks);
      if (refreshUserPerks && currentUser) await refreshUserPerks(currentUser.uid);
      const awarded = Number(result?.creditsAwarded) || 0;
      if (awarded > 0 && pip?.showToast) {
        pip.showToast(`✦ Walkthrough bonus · +${awarded} credits`);
      }
    } catch (err) {
      console.warn("Fan onboarding complete failed:", err);
    } finally {
      setBusy(false);
    }
  };

  const advance = () => {
    if (isLast) {
      finish();
      return;
    }
    setStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  return (
    <>
      <div className="pip-onboarding-backdrop" role="presentation" />
      <div className="pip-onboarding-wrap" role="dialog" aria-modal="true" aria-label="Pip walkthrough">
        <PipCharacter size={90} state={current.mood} peek={current.peek || undefined} />
        <div className="pip-onboarding-bubble">
          <p>{current.body}</p>
          <div className="pip-onboarding-steps" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`pip-onboarding-dot${i === step ? " is-active" : ""}`}
              />
            ))}
          </div>
          <button type="button" onClick={advance} disabled={busy}>
            {current.action}
          </button>
        </div>
      </div>
    </>
  );
}
